
/**
 * Utilitários para conversão de fuso horário
 * 
 * Este arquivo centraliza todas as conversões entre:
 * - Horário local de São Paulo (UTC-3)
 * - UTC para salvar no banco de dados
 * 
 * Regras:
 * - Frontend → Banco: Adicionar 3h (SP → UTC)
 * - Banco → Frontend: Subtrair 3h (UTC → SP)
 */

import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const SAO_PAULO_UTC_OFFSET_HOURS = 3;
const SAO_PAULO_LOCAL_DATE_TIME_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

interface ParsedSaoPauloLocalDateTime {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const isValidCalendarDate = (year: number, month: number, day: number) => {
  // Motivo: validar o calendário evita aceitar datas impossíveis como 30/02 e só então converter para UTC.
  const referenceDate = new Date(Date.UTC(year, month - 1, day));

  return (
    referenceDate.getUTCFullYear() === year &&
    referenceDate.getUTCMonth() === month - 1 &&
    referenceDate.getUTCDate() === day
  );
};

const parseSaoPauloLocalDateTime = (saoPauloDateTime: string): ParsedSaoPauloLocalDateTime => {
  // Motivo: `datetime-local` não traz offset; fazer parse manual impede que cada navegador injete o próprio timezone na conversão.
  const sanitizedDateTime = saoPauloDateTime.trim();
  const match = SAO_PAULO_LOCAL_DATE_TIME_REGEX.exec(sanitizedDateTime);

  if (!match) {
    throw new Error('Data/hora inválida. Use o formato YYYY-MM-DDTHH:mm.');
  }

  const [, yearText, monthText, dayText, hourText, minuteText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    !isValidCalendarDate(year, month, day)
  ) {
    throw new Error('Data/hora de São Paulo inválida. Confira dia e horário informados.');
  }

  return { year, month, day, hour, minute };
};

/**
 * Converte horário de São Paulo para UTC (para salvar no banco)
 * Exemplo: 13/07 23:40 SP → 14/07 02:40 UTC
 */
export const convertSaoPauloToUTC = (saoPauloDateTime: string): string => {
  // Motivo: o domínio atual usa São Paulo em UTC-3 fixo; somar 3 horas no relógio local gera o instante UTC correto sem depender do fuso do navegador.
  const { year, month, day, hour, minute } = parseSaoPauloLocalDateTime(saoPauloDateTime);
  const utcDate = new Date(Date.UTC(year, month - 1, day, hour + SAO_PAULO_UTC_OFFSET_HOURS, minute));

  return utcDate.toISOString();
};

/**
 * Converte horário UTC do banco para São Paulo (para exibir)
 * Exemplo: 14/07 02:40 UTC → 13/07 23:40 SP
 */
export const convertUTCToSaoPaulo = (utcDateTime: string): Date => {
  // Input: "2025-07-14T02:40:00.000Z"
  const utcDate = parseISO(utcDateTime);
  
  // ✅ CORRETO: Adicionar 3 horas (SP está 3h atrás do UTC)
  const saoPauloDate = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000));
  
  return saoPauloDate;
};

/**
 * Formatar data no padrão brasileiro DD/MM/AAAA HH:mm
 */
export const formatBrazilianDateTime = (date: Date): string => {
  return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
};

/**
 * Obter data atual em São Paulo no formato YYYY-MM-DD
 */
export const getTodayInSaoPaulo = (): string => {
  const now = new Date();
  // Ajustar para horário de SP (subtrair 3h do UTC)
  const saoPauloNow = new Date(now.getTime() - (3 * 60 * 60 * 1000));
  return saoPauloNow.toISOString().split('T')[0];
};

/**
 * Obter hora atual em São Paulo no formato HH:mm
 */
export const getCurrentTimeInSaoPaulo = (): string => {
  const now = new Date();
  // Ajustar para horário de SP (subtrair 3h do UTC)
  const saoPauloNow = new Date(now.getTime() - (3 * 60 * 60 * 1000));
  return saoPauloNow.toTimeString().slice(0, 5);
};

/**
 * Verificar se uma data/hora está no passado (baseado em SP)
 */
export const isDateTimeInPast = (dateTime: string): boolean => {
  const inputDate = new Date(dateTime);
  const now = new Date();
  const saoPauloNow = new Date(now.getTime() - (3 * 60 * 60 * 1000));
  
  return inputDate < saoPauloNow;
};
